import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { signInWithCognito } from 'src/api/auth'
import { useLoginUser } from 'src/api/user'
import {
  CREATE_STATUS,
  EMPTY_LOGIN_USER,
  EMPTY_USER,
  LoginUser,
  User,
} from 'src/models/User'
import { shouldDisplayPaidPlanLabel } from 'src/models/User/role'
import { AuthService } from 'src/services/AuthService'
import { IS_DEVELOPMENT } from 'src/utils/env'
import { POLICY, SIGN_IN } from 'src/utils/routes'
import { ExchangeHOC } from 'src/utils/types'
import { shouldIgnorePath } from '../client'
import { useUser } from './hooks'

type withLoginUserHoc = ExchangeHOC<{ user: LoginUser; loggedIn: boolean }>

const intializeAuth = async (onInitialized: VoidFunction) => {
  try {
    const token = await AuthService.initialize()
    if (token) await signInWithCognito(token)
  } finally {
    onInitialized()
  }
}
const clearSession = async () => {
  try {
    await AuthService.signOut()
  } finally {
    window.location.replace(SIGN_IN)
  }
}
export const withLoginUser: withLoginUserHoc = (Compnent) => (props) => {
  const [authInitialized, setAuthInitialized] = useState(false)
  const { user, isValidating } = useLoginUser({
    dedupingInterval: IS_DEVELOPMENT ? undefined : 300000,
  })
  const router = useRouter()

  useEffect(() => {
    intializeAuth(() => setAuthInitialized(true))
  }, [])
  useEffect(() => {
    if (!IS_DEVELOPMENT && authInitialized && !isValidating && !user) {
      setAuthInitialized(false)
      // NOTE: /contents などのページでログアウト処理が走らないように
      const shouldIgnore = shouldIgnorePath(router.pathname)
      if (shouldIgnore) return
      clearSession()
    }
  }, [authInitialized, isValidating, user, router.pathname])

  if (!IS_DEVELOPMENT && authInitialized && !isValidating && !user) return <></>

  // NOTE: 無料会員 & create_status が ログイン済み の場合は1度だけ同意画面を開く
  if (
    router.pathname !== POLICY &&
    user?.plan.freeAndPartner &&
    !user?.isAgreedPolicy &&
    user?.company.createStatus == CREATE_STATUS.LOGIN
  ) {
    router.push(POLICY)
    return <></>
  }

  return (
    <Compnent
      {...props}
      user={user || EMPTY_LOGIN_USER}
      loggedIn={user ? true : false}
    />
  )
}

type WithUserHoc = ExchangeHOC<
  { user: User },
  { id: number; close: () => void }
>

export const withUser: WithUserHoc = (Compnent) => (props) => {
  const { data, mutate, error } = useUser(props.id, {
    dedupingInterval: IS_DEVELOPMENT ? undefined : 300000,
  })
  const { user: loginUser } = useLoginUser({
    revalidateOnMount: false,
  })

  if (!data || !loginUser) return <></>

  if (error)
    return <Compnent {...props} user={EMPTY_USER} close={props.close} />
  return (
    <Compnent
      {...props}
      user={data || EMPTY_USER}
      close={props.close}
      displayCertified={shouldDisplayPaidPlanLabel([
        loginUser && loginUser.plan.role,
      ])}
      mutate={mutate}
    />
  )
}
